import React, { useCallback, useMemo, useState } from 'react';
import { match } from 'variant';
import kebabCase from 'lodash/kebabCase.js';
import type { SelectGroup } from '@wix/cli-ui-kit';
import {
  Text,
  TextInput,
  SelectInput,
  SelectGroupsInput,
  Spinner,
  Link,
} from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { useDevCenterClient } from '@wix/dev-center-client';
import type {
  WidgetPlugin,
  Placement,
  RequiredApp,
} from '@wix/extensions-data-client';
import {
  getEcomRequiredApps,
  useExtensionDataClient,
} from '@wix/extensions-data-client';
import uniqBy from 'lodash/uniqBy.js';
import {
  useCustomElementDirectoryValidation,
  useCustomElementNameValidation,
} from '../../../dev-center/components/validations/index.js';
import { useGenerationTask } from '../hooks/use-generation-task.js';
import { generateSitePlugin } from './generate.js';
import { useProjectModel } from '../../../model.js';
import { useWixCliAppBi } from '../../../bi/index.js';
import { AppRequirement } from '../AppRequirement.js';
import { MultipleAppRequirement } from '../MultipleAppRequirement.js';
import { InstallDependencies } from '../InstallDependencies.js';
import { GenerationSuccessMessage } from '../GenerationSuccessMessage.js';

export const SitePluginWizard: React.FC<{ widgetPlugins: WidgetPlugin[] }> = ({
  widgetPlugins,
}) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const { addRequiredApp } = useDevCenterClient();
  const { getWidgetDocsUrl } = useExtensionDataClient();

  const pluginApplications = useMemo(
    () =>
      uniqBy(widgetPlugins, 'appName').map((pluginPlacement) => ({
        value: pluginPlacement.appName,
        title: pluginPlacement.appName,
      })),
    [widgetPlugins]
  );

  const extensionType = t('generate_command.flow.site_plugin').toLowerCase();

  const validateFolderName = useCustomElementDirectoryValidation('Site plugin');
  const validateName = useCustomElementNameValidation();

  const [slot, setSlot] = useState<{
    placement: Placement;
    requiredApps: RequiredApp[];
  }>();
  const [requireAppsIds, setRequireAppsIds] = useState<string[]>();
  const [placementApp, setPlacementApp] = useState<string>();
  const [pluginFolder, setPluginFolder] = useState<string>();
  const [pluginName, setPluginName] = useState<string>();
  const [finishedInstallation, setFinishedInstallation] =
    useState<boolean>(false);

  const defaultPluginFolder = kebabCase(pluginName);

  const widgetSlots = useMemo(
    () =>
      placementApp
        ? widgetPlugins
            .filter((widgetPlugin) => widgetPlugin.appName === placementApp)
            .reduce<Record<string, SelectGroup<Placement>>>(
              (_widgetSlots, widgetPlugin) => {
                const widgetDocsUrl = getWidgetDocsUrl(
                  widgetPlugin.component.componentId
                );

                const description = widgetDocsUrl && (
                  <Text skin="secondary">
                    <Trans
                      i18nKey="generate_command.site_plugin.widget_docs"
                      components={[<Link url={widgetDocsUrl} />]}
                    />
                  </Text>
                );

                _widgetSlots[widgetPlugin.component.componentId] ??= {
                  title: widgetPlugin.component.name,
                  description,
                  items: [],
                };

                for (const placement of widgetPlugin.component.data.widgetPlugin
                  .placements) {
                  _widgetSlots[widgetPlugin.component.componentId]?.items.push({
                    key: placement.slotId,
                    title: placement.slotId,
                    value: placement,
                  });
                }

                return _widgetSlots;
              },
              {}
            )
        : {},
    [widgetPlugins, placementApp, getWidgetDocsUrl]
  );

  const generationTask = useGenerationTask(async () => {
    if (pluginName && slot && requireAppsIds) {
      const requireAppId = requireAppsIds.at(0);

      if (requireAppId) {
        await addRequiredApp({
          appId: model.config.appId,
          appIdToRequire: requireAppId,
        });
      }

      const { appDefinitionId, widgetId, slotId } = slot.placement;

      const widgetPlugin = widgetSlots[widgetId];

      if (widgetPlugin) {
        return generateSitePlugin(
          model.projectFolder,
          [{ appDefinitionId, widgetId, slotId }],
          pluginFolder ?? defaultPluginFolder,
          pluginName,
          widgetPlugin.title,
          getWidgetDocsUrl(widgetId)
        );
      }
    }
  }, [pluginFolder, pluginName, slot, widgetSlots, getWidgetDocsUrl]);

  const handleSetSlot = useCallback(
    (placement: Placement) => {
      void getEcomRequiredApps().then((ecomRequiredApp) => {
        const widgetPlugin = widgetPlugins.find(
          (widgetPlugin) =>
            widgetPlugin.component.componentId === placement.widgetId
        );

        if (ecomRequiredApp.appId === widgetPlugin?.appId) {
          setSlot({ requiredApps: ecomRequiredApp.requiredApps, placement });
        } else {
          setSlot({
            requiredApps: [
              {
                appId: widgetPlugin!.appId,
                appName: widgetPlugin!.appName,
              },
            ],
            placement,
          });
        }
      });
    },
    [widgetPlugins]
  );

  return (
    <>
      <SelectInput<string>
        label={t('generate_command.site_plugin.plugin_app')}
        info={
          <Trans
            i18nKey="generate_command.site_plugin.plugin_app.description"
            components={[<Link url="https://wix.to/Onfe4XJ" />]}
          />
        }
        options={pluginApplications}
        onSubmit={(option) => {
          bi.cliFlowStepAnswered({
            question: t('generate_command.site_plugin.plugin_app'),
            questionKey: 'generate_command.site_plugin.plugin_app',
            answer: option.title,
            additionalInfo: { ...option },
          });
          setPlacementApp(option.value);
        }}
      />
      {placementApp && (
        <SelectGroupsInput<Placement>
          label={t('generate_command.site_plugin.choose_placement')}
          groups={Object.values(widgetSlots)}
          onSubmit={(option) => {
            bi.cliFlowStepAnswered({
              question: t('generate_command.site_plugin.choose_placement'),
              questionKey: 'generate_command.site_plugin.choose_placement',
              answer: option.title,
              additionalInfo: option.value,
            });
            handleSetSlot(option.value);
          }}
        />
      )}
      {slot?.requiredApps &&
        (slot.requiredApps.length === 1 ? (
          <AppRequirement
            appId={model.config.appId}
            requiredApps={slot.requiredApps.map((requiredApp) => ({
              id: requiredApp.appId,
              name: requiredApp.appName,
            }))}
            onSubmit={setRequireAppsIds}
          />
        ) : (
          <MultipleAppRequirement
            appId={model.config.appId}
            requiredApps={slot.requiredApps.map((requiredApp) => ({
              id: requiredApp.appId,
              name: requiredApp.appName,
            }))}
            onSubmit={setRequireAppsIds}
          />
        ))}
      {requireAppsIds && (
        <TextInput
          label={t('generate_command.site_plugin.choose_name')}
          placeholder={t('generate_command.site_plugin.name_placeholder')}
          onSubmit={(value) => {
            bi.cliFlowStepAnswered({
              question: t('generate_command.site_plugin.choose_name'),
              questionKey: 'generate_command.site_plugin.choose_name',
              answer: value,
              additionalInfo: {
                extensionType,
              },
            });
            setPluginName(value);
          }}
          validate={validateName}
        />
      )}
      {pluginName && (
        <TextInput
          label={t('generate_command.site_plugin.choose_folder')}
          placeholder={defaultPluginFolder}
          onSubmit={(value) => {
            const finalValue = value || defaultPluginFolder;
            bi.cliFlowStepAnswered({
              question: t('generate_command.site_plugin.choose_folder'),
              questionKey: 'generate_command.site_plugin.choose_folder',
              answer: finalValue,
              additionalInfo: {
                extensionType,
              },
            });
            setPluginFolder(finalValue);
          }}
          validate={validateFolderName}
        />
      )}
      {useMemo(
        () =>
          match(generationTask, {
            Success: ({ hasUpdatedDependencies, newFiles }) => {
              if (!hasUpdatedDependencies) {
                setFinishedInstallation(true);
              }
              return (
                <>
                  {hasUpdatedDependencies && (
                    <InstallDependencies
                      onFinish={() => setFinishedInstallation(true)}
                    />
                  )}
                  {finishedInstallation && (
                    <GenerationSuccessMessage
                      extensionType={extensionType}
                      generatedFiles={newFiles}
                      // https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/wix-cli/supported-extensions/site-extensions/site-plugins/add-a-site-plugin-extension-in-the-cli
                      learnMoreUrl="https://wix.to/rEfCfbY"
                    />
                  )}
                </>
              );
            },
            Loading: () => (
              <Spinner
                text={t('generate_command.generation.loading', {
                  subject: extensionType,
                })}
              />
            ),
            Error: () => null,
            NotRequested: () => null,
          }),
        [
          generationTask,
          t,
          extensionType,
          finishedInstallation,
          setFinishedInstallation,
        ]
      )}
    </>
  );
};
